import React, { useMemo, useState } from "react";
import CustomDropdown from "../../components/shared/dropdown/CustomDropdown";
import { BarsSvg } from "../../svgFiles/BarsSvg";
import { UndoItemSvg } from "../../svgFiles/UndoItemSvg";
import { DownloadSvg } from "../../svgFiles/DownloadSvg";
import { SendTrackingsSvg } from "../../svgFiles/SendTrackingsSvg";
import { SendReminderSvg } from "../../svgFiles/SendReminderSvg";
import { useUndoOrderMutation, useDownloadOrdersExcelMutation, useSendPrescriptionMailMutation, useSendTrackingLinkMailMutation } from "../../app/orderApi/OrderApi";
import { useDownloadAllOrdersExcelMutation } from "../../app/orderApi/OrderApi";
import CommonModal from "../../components/shared/modalLayout/CommonModal";
import { showToast } from "../../helpers/Utils";
import { useAuth } from "../auth/AuthContext";
import { P } from "../../utils/permissions";

const QuickFilter = ({ selectedIds, selectedItems, onClearSelection, selectedShopifyIds}) => {
    const { hasPermission } = useAuth();
    const [selectedOption, setSelectedOption] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [actionToConfirm, setActionToConfirm] = useState(null);
    const [undoOrder] = useUndoOrderMutation();
    const [downloadSelectedOrders, { isLoading: isDownloadingSelected }] = useDownloadOrdersExcelMutation();
    const [SendTrackingLinkMail, { isLoading: isSendTrackingLinkMail }] = useSendTrackingLinkMailMutation();
    const [SendPrescriptionMail, { isLoading: isSendPrescriptionMail }] = useSendPrescriptionMailMutation();
    const [downloadAllOrders, { isLoading: isDownloadingAll }] = useDownloadAllOrdersExcelMutation();

    const options = useMemo(() => {
        const items = [];
        if (hasPermission(P.ORDERS_REDO)) {
            items.push({ icon: <UndoItemSvg />, label: "Undo a redo", value: "Undo a redo" });
        }
        if (hasPermission(P.ORDERS_EXPORT)) {
            items.push({ icon: <DownloadSvg />, label: "Download excel", value: "Download excel" });
        }
        if (hasPermission(P.ORDERS_SEND_TRACKING_MAIL)) {
            items.push({ icon: <SendTrackingsSvg />, label: "Send tracking link", value: "Send tracking link" });
        }
        if (hasPermission(P.PRESCRIPTION_SEND_MAIL)) {
            items.push({ icon: <SendReminderSvg />, label: "Send Prescription reminder", value: "Send rx reminder" });
        }
        return items;
    }, [hasPermission]);

    const handleDropdownSelect = (opt) => {
        setSelectedOption(opt?.value || null);
        if (!opt) return;

        setActionToConfirm(opt);
        setShowModal(true);
    };

    const handleConfirm = async () => {
        if (!actionToConfirm) return;
        try {
            const { value } = actionToConfirm;

            if (value === "Undo a redo") {
                const result = await undoOrder({ ShopifyItemID: selectedIds, ItemID: selectedItems }).unwrap();
                if (result?.data && Array.isArray(result.data)) {
                    result.data.forEach(item => {
                        const msg = item.deleted ? "Undo redo applied successfully" : "Action not applied";
                        showToast(`${item.ItemID}: ${msg}`, item.deleted ? "success" : "error");
                    });
                }
                onClearSelection?.();
                setShowModal(false);
            }

            if (value === "Download excel") {
                await downloadSelectedOrders({ ShopifyId: selectedShopifyIds }).unwrap();
                setShowModal(false);

            }

            if (value === "Send tracking link") {
                try {
                    const result = await SendTrackingLinkMail({ ItemID: selectedItems }).unwrap();
                    setShowModal(false);

                    const successes = result.data?.successes || [];
                    const failures = result.data?.failures || [];

                    successes.forEach(item => {
                        showToast(`${item.ItemID}: ${item.message}`, "success");
                    });


                    failures.forEach(item => {
                        showToast(`${item.ItemID}: ${item.error}`, "error");
                    });

                    if (successes.length === 0 && failures.length === 0) {
                        showToast("No tracking link emails were processed.", "info");
                    }
                } catch (err) {
                    console.error("Error while sending tracking link:", err);
                    showToast("Unexpected error while sending tracking link emails", "error");
                }
            }


            if (value === "Send rx reminder") {
                try {
                    const result = await SendPrescriptionMail({ ItemID: selectedItems }).unwrap();
                    setShowModal(false);

                    const successCount = result.data?.success?.length || 0;
                    const failedItems = result.data?.failed || [];
                    const failedCount = failedItems.length;
                    if (successCount > 0 && failedCount === 0) {
                        showToast(`Prescription emails sent successfully to ${successCount} item(s)`, "success");

                    } else if (successCount > 0 && failedCount > 0) {
                        showToast(`${successCount} sent, ${failedCount} failed`, "warning");

                        failedItems.forEach(item => {
                            showToast(`${item.ItemID}: ${item.error}`, "error");
                        });

                    } else if (failedCount > 0) {
                        failedItems.forEach(item => {
                            showToast(`${item.ItemID}: ${item.error}`, "error");
                        });
                    }

                } catch (err) {
                    console.error("Error while sending rx reminder:", err);
                    showToast("Unexpected error while sending prescription emails", "error");
                }
            }

            if (value === "Download all orders") {
                await downloadAllOrders({}).unwrap();
                setShowModal(false);
            }


            setActionToConfirm(null);
        } catch (error) {
            console.error("Action failed:", error);
        }
    };

    if (options.length === 0) return null;

    return (
        <div>
            <CustomDropdown
                barIcon={<BarsSvg />}
                options={options}
                value={selectedOption}
                onSelect={handleDropdownSelect}
            />

            <CommonModal
                show={showModal}
                setShow={setShowModal}
                title="Confirm Action"
                centered
                className="modal-sm"
                onConfirm={handleConfirm}
                confirmLoading={isDownloadingSelected || isSendPrescriptionMail || isSendTrackingLinkMail || isDownloadingAll}
            >
                <p className="pt-3 pb-3">{`Are you sure you want to "${actionToConfirm?.label}"?`}</p>
            </CommonModal>
        </div>
    );
};

export default QuickFilter;
